// Neu (Fehlerbericht 01.10.) — ein Auffangkorb meldet seinen Absturz an den Server.
//
// Anlass: /d/ROT28-D01/s/1 zeigte „Hier ist etwas schiefgegangen“, und im Server-Log stand
// nichts — der Absturz geschah beim ersten Zeichnen, bevor eine Anfrage hinausging. Jetzt geht
// ein Bericht an POST /api/oberflaeche/fehler und landet in `docker logs`.
//
// E13: Der Bericht trägt NIE Seitentext, Anfragekörper oder Inhalt. Nur
//   route   das Routenmuster (`/d/:dok/s/:n`), nicht der Pfad mit Codes
//   fehler  die Klasse (`TypeError`)
//   meldung entschärft (Zitate → „…“, Mailadressen weg), ≤ 300 Zeichen
//   stapel  Komponentenstapel, ≤ 2 KB (Komponentennamen + Bündelorte)
//   build   Name des Einstiegsbündels (`index-DhripT_g`)
// Der Server entschärft dasselbe noch einmal (routers/oberflaeche.py) — doppelt hält besser.
import { matchRoutes } from "react-router-dom";
import { meldeOberflaechenFehler } from "../api/client";
import type { OberflaechenFehlerBericht } from "../api/typen";

type Routen = Parameters<typeof matchRoutes>[0];

export const MELDUNG_MAX = 300;
export const STAPEL_MAX = 2048;
/** Je Seitenaufruf höchstens so viele Berichte; die Grenze des Servers (30/h) bleibt maßgeblich. */
export const BERICHTE_MAX = 10;

let routen: Routen = [];
/** App.tsx meldet hier ihre Wegtabelle an (ein Import in die Gegenrichtung wäre ein Kreis). */
export function routenSetzen(r: Routen): void {
  routen = r;
}

/** Die festen Wegwörter der Wegtabelle (App.tsx) — eine Positivliste. Alles andere wird `:x`,
 *  auch ein kleingeschriebener Slug (`/v/MU26/themen/mobbing-auf-dem-schulhof`: Themen-Slugs
 *  entstehen aus Themennamen, Review E26). Gleiche Liste in backend routers/oberflaeche.py;
 *  test/fehlerbericht.test.tsx prüft, dass jedes feste Wort der Wegtabelle hier steht. */
export const FESTE_WEGE: ReadonlySet<string> = new Set([
  "a", "admin", "anmelden", "auswerten", "b", "boegen", "bogenvorlage", "d", "eingang", "exporte", "fragen", "gold",
  "k", "kodieren", "karte", "lauf", "m", "orte", "pruefen", "q", "r", "s", "t", "themen", "unbekannt", "v", "verfahren",
  "x", "zeichnungen",
]);

/** Feste Wegwörter bleiben; alles andere (Codes, uuids, Zahlen, Slugs) wird `:x`. */
export function grobMuster(pfad: string): string {
  const teile = pfad.split(/[?#]/)[0]!.split("/").slice(1);
  return "/" + teile.map((t) => (t === "" || FESTE_WEGE.has(t) ? t : ":x")).join("/");
}

/** `/d/ROT28-D01/s/1` → `/d/:dok/s/:n` (aus der Wegtabelle), sonst das grobe Muster. */
export function routenMuster(pfad: string, tabelle: Routen = routen): string {
  const treffer = tabelle.length ? matchRoutes(tabelle, pfad) : null;
  const muster = treffer
    ?.map((m) => m.route.path)
    .filter((p): p is string => !!p)
    .pop();
  return muster && muster !== "*" ? muster : grobMuster(pfad);
}

/** Ein Zitat mit dem Wort davor (`reading 'x'`). */
const ZITAT = /(\b[A-Za-z]+\s+)?(["'`])(.*?)\2/g;
const BEZEICHNER = /^[A-Za-z_$][\w$.]{0,40}$/;
/** Nur nach diesen Wörtern ist ein Zitat ein Programmbezeichner: Chrome „(reading 'x')“, Firefox
 *  „can't access property "x"“, Safari „(evaluating 'a.b')“, „… of 'x'“. Ein einzelnes Wort in
 *  Anführungszeichen woanders kann Inhalt sein — JSON.parse zitiert die Eingabe: „Unexpected
 *  token 'L', "Lena" is not valid JSON“ (Review E26). */
const KONTEXT = /^(?:reading|property|evaluating|of)\s+$/i;

/** Zitate (außer Bezeichnern nach reading/property/evaluating/of) und Mailadressen ausblenden, kürzen. */
export function entschaerfen(text: string, grenze = MELDUNG_MAX): string {
  const t = text
    .replace(/[\u0000-\u001f\u007f]/g, " ")
    .replace(ZITAT, (ganz, vor: string | undefined, q: string, innen: string) =>
      vor && KONTEXT.test(vor) && BEZEICHNER.test(innen) ? ganz : `${vor ?? ""}${q}…${q}`,
    )
    .replace(/[^\s@]+@[^\s@]+/g, "…@…")
    .replace(/\s+/g, " ")
    .trim();
  return t.length <= grenze ? t : t.slice(0, grenze - 1) + "…";
}

/** Komponentenstapel → `at Name (datei.js:z:s)` je Zeile; Abfrageteile und alles andere weg, ≤ 2 KB. */
export function stapelKuerzen(roh: string | null | undefined): string {
  const zeilen: string[] = [];
  for (const z of (roh ?? "").split("\n")) {
    // Chrome: „    at Name (url:z:s)“; Firefox/Safari: „Name@url:z:s“.
    const m = /^\s*(?:(?:at|in)\s+([A-Za-z_$][\w$.]*)|([A-Za-z_$][\w$.]*)@)/.exec(z);
    if (!m) continue;
    const name = (m[1] ?? m[2])!;
    const d = /([\w-]{1,80}\.(?:m?js|jsx|tsx?))(?:[?#][^:\s)]*)?(:\d{1,7}:\d{1,7})?/.exec(z.slice(m[0].length));
    // Die Form „at Name (datei.js:z:s)“ bleibt: der Server liest den Stapel genauso.
    zeilen.push(`at ${name.slice(0, 60)}` + (d ? ` (${d[1]}${d[2] ?? ""})` : ""));
  }
  const t = zeilen.join("\n");
  return t.length <= STAPEL_MAX ? t : t.slice(0, STAPEL_MAX - 1) + "…";
}

/** Name des Einstiegsbündels — ändert sich mit jedem Build, braucht keine Build-Variable. */
export function buildKennung(): string {
  try {
    const s = document.querySelector<HTMLScriptElement>('script[type="module"][src*="/assets/"]');
    const name = s?.src.split("/").pop()?.replace(/\.js(\?.*)?$/, "") ?? "";
    return name.replace(/[^\w.-]/g, "").slice(0, 40) || "dev";
  } catch {
    return "dev";
  }
}

/** Baut den Bericht. Rein — nimmt Pfad und Build als Argumente (testbar). */
export function berichtBauen(
  fehler: unknown,
  komponentenStapel: string | null | undefined,
  ort: OberflaechenFehlerBericht["ort"],
  pfad: string,
  build: string,
): OberflaechenFehlerBericht {
  const e = fehler instanceof Error ? fehler : null;
  const name = e?.name && /^[A-Za-z_$][\w$]{0,60}$/.test(e.name) ? e.name : "Error";
  return {
    route: routenMuster(pfad),
    fehler: name,
    meldung: entschaerfen(e?.message ?? (typeof fehler === "string" ? fehler : "")),
    stapel: stapelKuerzen(komponentenStapel),
    build,
    ort,
  };
}

const gemeldet = new Set<string>();

/**
 * Absturz melden — feuert und vergisst. Wirft nie (ein Fehler beim Melden darf den
 * Auffangkorb nicht selbst umwerfen); derselbe Absturz geht je Seitenaufruf nur einmal raus.
 */
export function fehlerMelden(fehler: unknown, komponentenStapel: string | null | undefined, ort: OberflaechenFehlerBericht["ort"]): void {
  try {
    const bericht = berichtBauen(fehler, komponentenStapel, ort, window.location.pathname, buildKennung());
    const schluessel = `${bericht.fehler}|${bericht.route}|${bericht.meldung}`;
    if (gemeldet.has(schluessel) || gemeldet.size >= BERICHTE_MAX) return;
    gemeldet.add(schluessel);
    void meldeOberflaechenFehler(bericht).catch(() => {
      /* 401/429/Netz: der Bericht ist verzichtbar */
    });
  } catch {
    /* siehe oben */
  }
}

/** Nur für Tests. */
export function _gemeldetLeeren(): void {
  gemeldet.clear();
}
